// src/pages/VehicleV2/VehicleTransfer.jsx
// ASPX: vehicletransfer.aspx + vehicletransfer.aspx.cs
// New / Edit vehicle transfer entry. No page-rights flow.

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaSave } from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/vehicleTransfer`;

const getLS = (key, fallback = "") => {
  if (typeof window === "undefined") return fallback;
  const val = localStorage.getItem(key);
  return val !== null && val !== undefined ? val : fallback;
};
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate()
  ).padStart(2, "0")}`;
};
const dmyToYmd = (v) => {
  const s = String(v || "").trim();
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  return todayYmd();
};
const optId = (row, ...keys) => {
  for (const k of keys) {
    if (row?.[k] !== undefined && row?.[k] !== null && String(row[k]) !== "") {
      return String(row[k]);
    }
  }
  return "";
};
const optName = (row, ...keys) => {
  for (const k of keys) {
    if (row?.[k] !== undefined && row?.[k] !== null && String(row[k]) !== "") {
      return String(row[k]);
    }
  }
  return "";
};

const CONDITIONS = ["Select", "Good Condition", "unCondition", "Damaged"];

const YesNo = ({ name, value, onChange, disabled, labelClass, isDark }) => (
  <div>
    <label className={labelClass}>{name}</label>
    <div className="flex gap-6">
      {[
        { t: "Yes", v: "0" },
        { t: "No", v: "1" },
      ].map((o) => (
        <label key={o.v} className="inline-flex items-center gap-2">
          <input
            type="radio"
            name={name}
            value={o.v}
            checked={String(value) === o.v}
            onChange={() => onChange(o.v)}
            disabled={disabled}
          />
          <span className={isDark ? "text-gray-300" : "text-gray-700"}>{o.t}</span>
        </label>
      ))}
    </div>
  </div>
);

const VehicleTransfer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = location.state || {};

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS =
    getLS("branchId") || getLS("Branchid") || getLS("branchid") || "";
  const branchTypeLS = getLS("branchType") || getLS("branchtype") || "HO";
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId") || "0";

  const branchId = useMemo(() => String(toInt(branchIdLS, 0)), [branchIdLS]);
  const branchType = useMemo(
    () => String(branchTypeLS || "").trim() || "HO",
    [branchTypeLS]
  );
  const userId = useMemo(() => String(userIdLS || "").trim() || "0", [userIdLS]);
  const loginUserId =
    (typeof window !== "undefined"
      ? sessionStorage.getItem("loginUserId")
      : "") || userId;

  const incomingVehicleId =
    navState.VehicleID || navState.vehicleId || navState.vehicleid || "";
  const incomingTransferId =
    navState.vehtransferid || navState.transferId || navState.transId || "";
  const isEdit = navState.VTflag === true;

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fromBranchName, setFromBranchName] = useState("");
  const [lockedVehicle, setLockedVehicle] = useState(!!incomingVehicleId);

  const [dd, setDd] = useState({
    toBranches: [],
    vehicleTypes: [],
    vehicleCodes: [],
    vehicleRegNos: [],
    fromStores: [],
    fromLocations: [],
    toLocations: [],
    toStores: [],
  });

  const [form, setForm] = useState({
    transferType: navState.transfertype === "L" ? "L" : "B",
    transferMode: navState.transmode === "V" ? "V" : "R",
    transferModeText: "",
    vehicleTypeId: navState.subtypeid ? String(navState.subtypeid) : "Select",
    vehicleId: incomingVehicleId ? String(incomingVehicleId) : "Select",
    transferDate: navState.TransDate ? dmyToYmd(navState.TransDate) : todayYmd(),
    toBranchId: "Select",
    reason: "",
    fromStoreId: "Select",
    fromLocationId: "Select",
    toLocationId: "Select",
    toStoreId: "Select",
    rcBook: "0",
    toolBox: "0",
    logNote: "0",
    spareManual: "0",
    spareTyre: "0",
    duplicateKey: "0",
    permit: "0",
    insurance: "0",
    tax: "0",
    operatorManual: "0",
  });

  const [batteries, setBatteries] = useState([]);
  const [tyres, setTyres] = useState([]);

  const inputClass = useMemo(
    () =>
      `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white"
          : "bg-white border-gray-300 text-gray-900"
      }`,
    [isDark]
  );
  const labelClass = useMemo(
    () =>
      `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const setField = (name, value) =>
    setForm((p) => ({
      ...p,
      [name]: value,
    }));

  const mapBattery = (r) => ({
    vehicleId: r.fld_vehicleid ?? r.vehicleId,
    batteryId: r.fld_batteryid ?? r.batteryId,
    serialNo: r.fld_battery_serialno ?? r.fld_serialNo ?? r.serialNo,
    condition: r.fld_condition || "Select",
    remarks: r.fld_remarks || "",
  });
  const mapTyre = (r) => ({
    vehicleId: r.fld_vehicleid ?? r.vehicleId,
    tyreId: r.fld_tyreId ?? r.fld_tyreid ?? r.tyreId,
    serialNo: r.fld_Tyreserialno ?? r.fld_serialNo ?? r.serialNo,
    condition: r.fld_condition || "Select",
    remarks: r.fld_remarks || "",
  });

  const loadInit = useCallback(async () => {
    if (!toInt(branchId) || !toInt(loginUserId)) {
      toast.error("branchId / loginUserId missing");
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          branchId,
          vehicleId: incomingVehicleId || "",
          transferId: incomingTransferId || "",
          VTflag: isEdit ? "true" : "false",
        },
        headers: commonHeaders,
      });
      if (!data?.success) throw new Error(data?.message || "Init failed");

      setDd((p) => ({ ...p, ...(data.dropdowns || {}) }));
      setFromBranchName(data.fromBranchName || navState.BranchName || "");
      setBatteries((data.batteries || []).map(mapBattery));
      setTyres((data.tyres || []).map(mapTyre));

      const h = data.header;
      if (h) {
        setForm((p) => ({
          ...p,
          vehicleId: String(h.fld_vehicleid || p.vehicleId),
          vehicleTypeId: String(
            h.fld_vehicletypeid || h.fld_vehicle_subtypeid || p.vehicleTypeId
          ),
          fromStoreId: String(h.fld_storeid || p.fromStoreId),
          fromLocationId: String(
            h.fld_fromlocationid || h.fld_locationid || p.fromLocationId
          ),
          toBranchId: String(h.fld_vehicle_tobranchid || p.toBranchId),
          toLocationId: String(h.fld_tolocationid || p.toLocationId),
          toStoreId: String(h.fld_tostoreid || p.toStoreId),
          reason: h.fld_reason || p.reason,
          transferMode: h.fld_vehicle_transfer_mode === "V" ? "V" : p.transferMode,
          transferModeText: h.fld_vehicle_transfernamecode || p.transferModeText,
          transferType:
            h.fld_veh_transfer_type === "L" ? "L" : p.transferType,
          rcBook: String(h.fld_rcbook ?? p.rcBook),
          toolBox: String(h.fld_toolbox ?? p.toolBox),
          logNote: String(h.fld_batterycondition ?? p.logNote),
          spareManual: String(h.fld_sparepartsmanual ?? p.spareManual),
          spareTyre: String(h.fld_sparetype ?? p.spareTyre),
          duplicateKey: String(h.fld_duplicatekey ?? p.duplicateKey),
          operatorManual: String(h.fld_operatormanual ?? p.operatorManual),
          permit: String(h.fld_permit ?? p.permit),
          insurance: String(h.fld_insurance ?? p.insurance),
          tax: String(h.fld_tax ?? p.tax),
        }));
        if (h.fld_vehicleid) setLockedVehicle(true);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Init failed");
    } finally {
      setLoading(false);
    }
  }, [
    branchId,
    loginUserId,
    incomingVehicleId,
    incomingTransferId,
    isEdit,
    commonHeaders,
    navState.BranchName,
  ]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const onToBranchChange = async (value) => {
    setField("toBranchId", value);
    setForm((p) => ({ ...p, toLocationId: "Select", toStoreId: "Select" }));
    if (!value || value === "Select") {
      setDd((p) => ({ ...p, toLocations: [], toStores: [] }));
      return;
    }
    try {
      const { data } = await axios.get(`${API_BASE}/to-branch-details`, {
        params: { toBranchId: value },
        headers: commonHeaders,
      });
      setDd((p) => ({
        ...p,
        toLocations: data.toLocations || [],
        toStores: data.toStores || [],
      }));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load To Location / Store");
    }
  };

  const onStoreChange = async (value) => {
    setField("fromStoreId", value);
    if (!value || value === "Select") return;
    try {
      const { data } = await axios.get(`${API_BASE}/by-store`, {
        params: { branchId, storeId: value },
        headers: commonHeaders,
      });
      setDd((p) => ({
        ...p,
        vehicleCodes: data.vehicleCodes?.length ? data.vehicleCodes : p.vehicleCodes,
        vehicleRegNos: data.vehicleRegNos?.length ? data.vehicleRegNos : p.vehicleRegNos,
        vehicleTypes: data.vehicleTypes?.length ? data.vehicleTypes : p.vehicleTypes,
      }));
    } catch {
      /* keep existing lists */
    }
  };

  const onVehicleChange = async (vehicleId) => {
    setField("vehicleId", vehicleId);
    if (!vehicleId || vehicleId === "Select") return;
    try {
      const { data } = await axios.get(`${API_BASE}/vehicle-sync`, {
        params: { vehicleId },
        headers: commonHeaders,
      });
      const v = data.vehicle;
      if (!v) return;
      setForm((p) => ({
        ...p,
        vehicleId: String(v.fld_vehicleid),
        vehicleTypeId: String(v.fld_vehicle_subtypeid || v.fld_vehicletypeid || p.vehicleTypeId),
        fromStoreId: String(v.fld_storeid || p.fromStoreId),
        fromLocationId: String(v.fld_locationid || p.fromLocationId),
      }));
    } catch {
      /* ignore */
    }
  };

  const validate = () => {
    if (!form.transferDate) return "Please Enter Transfer Date";
    if (form.transferDate > todayYmd()) return "Select Transfer Date lesser than current date";
    if (form.fromStoreId === "Select") return "Please Select the store";
    if (form.toStoreId === "Select") return "Please Select the ToStore";
    if (form.vehicleTypeId === "Select") return "Please Select Equipment Type";
    if (!form.vehicleId || form.vehicleId === "Select") return "Please Select Equipment Code";
    if (form.transferType === "B" && form.toBranchId === "Select") {
      return "Please Select Tranfer to Branch";
    }
    if (form.transferType === "L") {
      if (form.fromLocationId === "Select") return "Please Select From Location";
      if (form.toLocationId === "Select") return "Please Select To Location";
    }
    if (form.transferMode === "V" && !String(form.transferModeText).trim()) {
      return "Please Select Reg.Number";
    }
    return "";
  };

  const handleSubmit = async () => {
    const msg = validate();
    if (msg) {
      toast.error(msg);
      return;
    }
    try {
      setSaving(true);
      const { data } = await axios.post(
        `${API_BASE}/save`,
        {
          isEdit,
          branchId,
          loginUserId,
          vehicleId: form.vehicleId,
          vehicleTypeId: form.vehicleTypeId,
          transferId: incomingTransferId,
          transferType: form.transferType,
          transferMode: form.transferMode,
          transferModeText: form.transferModeText,
          transferDate: form.transferDate,
          toBranchId: form.toBranchId,
          fromLocationId: form.fromLocationId,
          toLocationId: form.toLocationId,
          fromStoreId: form.fromStoreId,
          toStoreId: form.toStoreId,
          reason: form.reason,
          toolbox: form.toolBox,
          spareTyre: form.spareTyre,
          spareManual: form.spareManual,
          rcBook: form.rcBook,
          operatorManual: form.operatorManual,
          duplicateKey: form.duplicateKey,
          logNote: form.logNote,
          permit: form.permit,
          insurance: form.insurance,
          tax: form.tax,
          batteries,
          tyres,
        },
        { headers: commonHeaders }
      );
      if (!data?.success) {
        toast.error(data?.message || "Save failed");
        return;
      }
      toast.success(data.message || (isEdit ? "Updated Successfully" : "Saved Successfully"));
      setTimeout(() => navigate("/vehicle/VehicleTransferListing"), 600);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const locationMode = form.transferType === "L";
  const onRoad = form.transferMode === "V";
  const busy = loading || saving;

  const codeOptions = dd.vehicleCodes;
  const regnoOptions = dd.vehicleRegNos;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TRANSFER</span>}
          setSkin={setSkin}
          currentSkin={currentSkin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div>
              <h2 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Transfer Entry
              </h2>
              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                INFRA SCREEN : B-23/1 • {isEdit ? "Edit" : "New"} • Branch {fromBranchName || branchId}
              </div>
            </div>
            <button
              type="button"
              onClick={() => navigate("/vehicle/VehicleTransferListing")}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold border ${
                isDark
                  ? "bg-gray-700 text-white border-gray-600"
                  : "bg-gray-100 text-gray-700 border-gray-300"
              }`}
            >
              <FaArrowLeft /> Back
            </button>
          </div>

          {loading ? (
            <div className="p-16 text-center">Loading...</div>
          ) : (
            <>
              <div className={`p-6 rounded-lg mb-6 ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
                <div className="flex flex-wrap gap-8 mb-6">
                  <div>
                    <label className={labelClass}>Transfer Type</label>
                    <div className="flex gap-6">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={!locationMode}
                          onChange={() => setField("transferType", "B")}
                          disabled={busy}
                        />
                        Branch Wise
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={locationMode}
                          onChange={() => setField("transferType", "L")}
                          disabled={busy}
                        />
                        Location Wise
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Transfer Mode</label>
                    <div className="flex gap-6">
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={!onRoad}
                          onChange={() => setField("transferMode", "R")}
                          disabled={busy}
                        />
                        Off Road
                      </label>
                      <label className="inline-flex items-center gap-2">
                        <input
                          type="radio"
                          checked={onRoad}
                          onChange={() => setField("transferMode", "V")}
                          disabled={busy}
                        />
                        On Road
                      </label>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className={labelClass}>Branch</label>
                    <div className={`px-4 py-2 rounded-lg border ${isDark ? "border-gray-600" : "border-gray-300"}`}>
                      {fromBranchName || "-"}
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>
                      Vehicle Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={inputClass}
                      value={form.vehicleTypeId}
                      disabled={busy || lockedVehicle}
                      onChange={(e) => setField("vehicleTypeId", e.target.value)}
                    >
                      <option value="Select">Select</option>
                      {dd.vehicleTypes.map((t, i) => (
                        <option
                          key={i}
                          value={optId(t, "fld_vehicletypeid", "fld_vehicle_subtypeid", "id")}
                        >
                          {optName(t, "fld_vehicletypename", "fld_vehicle_subtypename", "name")}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      Transfer Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      className={inputClass}
                      value={form.transferDate}
                      disabled
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Vehicle Registration Number <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={inputClass}
                      value={form.vehicleId}
                      disabled={busy || lockedVehicle}
                      onChange={(e) => onVehicleChange(e.target.value)}
                    >
                      <option value="Select">Select</option>
                      {regnoOptions.map((t, i) => (
                        <option key={i} value={optId(t, "fld_vehicleid", "id")}>
                          {optName(t, "fld_vehicle_regno", "name")}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      Vehicle Code <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={inputClass}
                      value={form.vehicleId}
                      disabled={busy || lockedVehicle}
                      onChange={(e) => onVehicleChange(e.target.value)}
                    >
                      <option value="Select">Select</option>
                      {codeOptions.map((t, i) => (
                        <option key={i} value={optId(t, "fld_vehicleid", "id")}>
                          {optName(t, "fld_vehicle_code", "name")}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      To Branch {form.transferType === "B" && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      className={inputClass}
                      value={form.toBranchId}
                      disabled={busy || locationMode}
                      onChange={(e) => onToBranchChange(e.target.value)}
                    >
                      <option value="Select">Select</option>
                      {dd.toBranches.map((t, i) => (
                        <option
                          key={i}
                          value={optId(t, "fld_Branchid", "fld_branchid", "id")}
                        >
                          {optName(t, "fld_BranchName", "fld_branchname", "name")}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="lg:col-span-2">
                    <label className={labelClass}>Reason for Transfer</label>
                    <textarea
                      className={inputClass}
                      rows={3}
                      value={form.reason}
                      disabled={busy}
                      onChange={(e) => setField("reason", e.target.value)}
                    />
                  </div>

                  <div>
                    <label className={labelClass}>
                      Store <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={inputClass}
                      value={form.fromStoreId}
                      disabled={busy || lockedVehicle}
                      onChange={(e) => onStoreChange(e.target.value)}
                    >
                      <option value="Select">Select</option>
                      {dd.fromStores.map((t, i) => (
                        <option key={i} value={optId(t, "fld_storeid", "id")}>
                          {optName(t, "fld_storename", "name")}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      From Location {locationMode && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      className={inputClass}
                      value={form.fromLocationId}
                      disabled={busy || lockedVehicle}
                      onChange={(e) => setField("fromLocationId", e.target.value)}
                    >
                      <option value="Select">Select</option>
                      {dd.fromLocations.map((t, i) => (
                        <option
                          key={i}
                          value={optId(t, "fld_Worklocationid", "fld_worklocationid", "id")}
                        >
                          {optName(t, "fld_Worklocationname", "fld_worklocationname", "name")}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      To Location {locationMode && <span className="text-red-500">*</span>}
                    </label>
                    <select
                      className={inputClass}
                      value={form.toLocationId}
                      disabled={busy}
                      onChange={(e) => setField("toLocationId", e.target.value)}
                    >
                      <option value="Select">Select</option>
                      {dd.toLocations.map((t, i) => (
                        <option
                          key={i}
                          value={optId(t, "fld_Worklocationid", "fld_worklocationid", "id")}
                        >
                          {optName(t, "fld_Worklocationname", "fld_worklocationname", "name")}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>
                      To Store <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={inputClass}
                      value={form.toStoreId}
                      disabled={busy}
                      onChange={(e) => setField("toStoreId", e.target.value)}
                    >
                      <option value="Select">Select</option>
                      {dd.toStores.map((t, i) => (
                        <option key={i} value={optId(t, "fld_storeid", "fld_Storeid", "id")}>
                          {optName(t, "fld_storename", "fld_Storename", "name")}
                        </option>
                      ))}
                    </select>
                  </div>

                  {onRoad && (
                    <div>
                      <label className={labelClass}>
                        Vehicle Number for transfer mode <span className="text-red-500">*</span>
                      </label>
                      <input
                        className={inputClass}
                        value={form.transferModeText}
                        disabled={busy}
                        onChange={(e) => setField("transferModeText", e.target.value)}
                      />
                    </div>
                  )}
                </div>
              </div>

              <h3 className="text-lg font-semibold mb-3">Vehicle battery Details</h3>
              <div className="overflow-x-auto rounded-lg border mb-6">
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-3 py-2 text-left text-sm">S.No</th>
                      <th className="px-3 py-2 text-left text-sm">Battery serial No</th>
                      <th className="px-3 py-2 text-left text-sm">Condition</th>
                      <th className="px-3 py-2 text-left text-sm">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batteries.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-sm">
                          No Records Found!
                        </td>
                      </tr>
                    ) : (
                      batteries.map((row, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2 text-sm">{idx + 1}</td>
                          <td className="px-3 py-2 text-sm">{row.serialNo || "-"}</td>
                          <td className="px-3 py-2">
                            <select
                              className={inputClass}
                              value={row.condition}
                              onChange={(e) =>
                                setBatteries((p) =>
                                  p.map((r, i) =>
                                    i === idx ? { ...r, condition: e.target.value } : r
                                  )
                                )
                              }
                            >
                              {CONDITIONS.map((c) => (
                                <option key={c} value={c}>
                                  {c === "unCondition" ? "uncondition" : c}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              className={inputClass}
                              value={row.remarks}
                              onChange={(e) =>
                                setBatteries((p) =>
                                  p.map((r, i) =>
                                    i === idx ? { ...r, remarks: e.target.value } : r
                                  )
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-semibold mb-3">Vehicle Tyre Details</h3>
              <div className="overflow-x-auto rounded-lg border mb-6">
                <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                  <thead className={`${tableHeaderClass} text-white`}>
                    <tr>
                      <th className="px-3 py-2 text-left text-sm">S.No</th>
                      <th className="px-3 py-2 text-left text-sm">Tyre serial No</th>
                      <th className="px-3 py-2 text-left text-sm">Condition</th>
                      <th className="px-3 py-2 text-left text-sm">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tyres.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-3 py-6 text-center text-sm">
                          No Records Found!
                        </td>
                      </tr>
                    ) : (
                      tyres.map((row, idx) => (
                        <tr key={idx}>
                          <td className="px-3 py-2 text-sm">{idx + 1}</td>
                          <td className="px-3 py-2 text-sm">{row.serialNo || "-"}</td>
                          <td className="px-3 py-2">
                            <select
                              className={inputClass}
                              value={row.condition}
                              onChange={(e) =>
                                setTyres((p) =>
                                  p.map((r, i) =>
                                    i === idx ? { ...r, condition: e.target.value } : r
                                  )
                                )
                              }
                            >
                              {CONDITIONS.map((c) => (
                                <option key={c} value={c}>
                                  {c === "unCondition" ? "uncondition" : c}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <input
                              className={inputClass}
                              value={row.remarks}
                              onChange={(e) =>
                                setTyres((p) =>
                                  p.map((r, i) =>
                                    i === idx ? { ...r, remarks: e.target.value } : r
                                  )
                                )
                              }
                            />
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <h3 className="text-lg font-semibold mb-3">Filtration area</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <YesNo name="RC Book" value={form.rcBook} onChange={(v) => setField("rcBook", v)} disabled={busy} labelClass={labelClass} isDark={isDark} />
                <YesNo name="Tool Box" value={form.toolBox} onChange={(v) => setField("toolBox", v)} disabled={busy} labelClass={labelClass} isDark={isDark} />
                <YesNo name="Log Note" value={form.logNote} onChange={(v) => setField("logNote", v)} disabled={busy} labelClass={labelClass} isDark={isDark} />
                <YesNo name="Spare Parts Manual" value={form.spareManual} onChange={(v) => setField("spareManual", v)} disabled={busy} labelClass={labelClass} isDark={isDark} />
                <YesNo name="Spare Tyre" value={form.spareTyre} onChange={(v) => setField("spareTyre", v)} disabled={busy} labelClass={labelClass} isDark={isDark} />
                <YesNo name="Duplicate Key" value={form.duplicateKey} onChange={(v) => setField("duplicateKey", v)} disabled={busy} labelClass={labelClass} isDark={isDark} />
                <YesNo name="Permit Copy" value={form.permit} onChange={(v) => setField("permit", v)} disabled={busy} labelClass={labelClass} isDark={isDark} />
                <YesNo name="Insurance Copy" value={form.insurance} onChange={(v) => setField("insurance", v)} disabled={busy} labelClass={labelClass} isDark={isDark} />
                <YesNo name="Tax" value={form.tax} onChange={(v) => setField("tax", v)} disabled={busy} labelClass={labelClass} isDark={isDark} />
                <YesNo name="Operator Manual" value={form.operatorManual} onChange={(v) => setField("operatorManual", v)} disabled={busy} labelClass={labelClass} isDark={isDark} />
              </div>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={busy}
                className={`inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold text-white ${buttonClass} ${hoverClass} ${
                  busy ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaSave />
                {saving ? "Saving..." : isEdit ? "Update" : "Submit"}
              </button>
            </>
          )}

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTransfer;